import { Link } from '@/components/ui/link';
import { Section, Title } from '@/components/ui/layout';
import { Mail } from 'lucide-react';
import { useEffect } from 'react';

import { SiteShell } from '@/components/site-shell';

const deletionEmail =
  'mailto:app@adventist.ro?' +
  new URLSearchParams({
    subject: 'Ștergere cont Zecimi și Daruri',
    body: 'Solicit ștergerea contului meu din aplicația Zecimi și Daruri și a datelor personale asociate. Trimit această cerere de la adresa de e-mail asociată contului meu.',
  }).toString();

/** Public instructions stay available even when the donor cannot open the app. */
export const DeleteAccountPage = (): JSX.Element => {
  useEffect(() => {
    document.title = 'Ștergerea contului · Zecimi și Daruri';
    return () => {
      document.title = 'Zecimi și Daruri';
    };
  }, []);

  return (
    <SiteShell>
      <main id="main-content" tabIndex={-1} className="legal-page">
        <article className="legal-document ui-stack">
          <p className="eyebrow">Zecimi și Daruri</p>
          <Title level={1}>Ștergerea contului Zecimi și Daruri</Title>
          <p className="legal-document__intro">
            Poți solicita ștergerea contului și a datelor personale asociate fără să reinstalezi
            aplicația și fără să te autentifici pe acest site.
          </p>
          <p>
            Cererile sunt prelucrate de Uniunea de Conferințe a Bisericii Adventiste de Ziua a
            Șaptea din România, operatorul platformei Zecimi și Daruri.
          </p>

          <Section flow>
            <Title level={2}>Cum trimiți cererea</Title>
            <ol>
              <li>
                Trimite un e-mail la <Link href="mailto:app@adventist.ro">app@adventist.ro</Link> de
                la adresa de e-mail asociată contului tău.
              </li>
              <li>
                Folosește subiectul „Ștergere cont Zecimi și Daruri” și precizează că soliciți
                ștergerea contului și a datelor personale asociate.
              </li>
              <li>
                Pentru protejarea datelor, putem solicita informații rezonabile de verificare a
                identității. Nu trimite parole, coduri de autentificare sau datele cardului.
              </li>
            </ol>
            <div className="button-row">
              <Link appearance="primary" href={deletionEmail}>
                <Mail size={16} aria-hidden /> Pregătește e-mailul de ștergere
              </Link>
            </div>
            <p>
              Butonul deschide o cerere precompletată în aplicația ta de e-mail. Cererea se trimite
              numai după ce apeși „Trimite” acolo. Poți folosi și adresa de mai sus direct din
              serviciul tău de e-mail.
            </p>
          </Section>

          <Section flow>
            <Title level={2}>Perioada de grație și anularea</Title>
            <p>
              După înregistrarea cererii de ștergere există o perioadă de grație de 14 zile. În
              această perioadă o poți anula din aplicație, în secțiunea „Datele mele și
              confidențialitate” a contului. Dacă nu este anulată, după perioada de grație contul
              este dezactivat și datele identificabile ale donatorului sunt anonimizate.
            </p>
          </Section>

          <Section flow>
            <Title level={2}>Ce date sunt șterse sau păstrate</Title>
            <p>
              Datele de identificare și contact ale contului și donatorului sunt anonimizate:
              numele, adresa de e-mail, telefonul și adresa poștală, dacă au fost furnizate. Accesul
              la cont este revocat. Înregistrările donațiilor rămân fără identificatori direcți ai
              donatorului.
            </p>
            <p>
              Documentele financiar-contabile, inclusiv chitanțele, și alte date necesare
              îndeplinirii obligațiilor legale pot fi păstrate pentru perioadele aplicabile.
              Termenele de păstrare și excepțiile de la ștergerea imediată sunt descrise în{' '}
              <Link to="/privacy">Politica de confidențialitate</Link>.
            </p>
          </Section>
        </article>
      </main>
    </SiteShell>
  );
};
